import { useEffect, useRef, useState } from "react";
import { Box, Grid, Slide, Typography, useTheme, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { Cancel, CheckCircle } from "@mui/icons-material";
import { useGetInternalUsersQuery } from "api/api-it";
import { useGetCurrentUserQuery } from "api/api-users-v2";
import { INCOMLEND_INTERNAL_LINK, IT_LINK } from "codes";
import useWidth from "helpers/useWidth";
import history from "helpers/history";
import DataTablev2 from "components/Common/DataTablev2";
import NoData from "components/Common/NoData";
import SkeletonLoad from "components/Common/SkeletonLoad";
import ShowUserGroups from "./ShowUserGroups";
import useIsMobile from "hooks/useIsMobile";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { clearAll } from "redux/reducers/searchReducer";

interface IUserIT {
  user_id: string;
  contact_id: string;
  first_name: string;
  last_name: string;
  username: string;
  last_login: Date;
  contact_created_at: string;
  is_active: boolean;
  departments: string[];
  groups: string[];
}

export default function AllUsers() {
  const isMobile = useIsMobile();
  const [userType, setUserType] = useState("Internal");
  const ref = useRef<HTMLDivElement>(null);
  const width = ref ? useWidth(ref) : 1000;
  const theme = useTheme();
  const searchValue = useAppSelector((state) => state.search.value);
  const dispatch = useAppDispatch();

  const { data: curr_user } = useGetCurrentUserQuery();
  const settings = curr_user?.settings ?? {};
  const rowsPerPage = settings?.rowsPerPage ?? 10;

  const [queryParams, setQueryParams] = useState({
    per_page: rowsPerPage,
    page: 1,
    search: searchValue || undefined,
  });
  const [filters, setFilters] = useState<Record<string, any>>({});

  const handleChange = (event: SelectChangeEvent) => {
    setUserType(event.target.value);
    setQueryParams((prev) => ({ ...prev, page: 1 }));
  };

  useEffect(() => {
    dispatch(clearAll());
  }, [dispatch, userType]);

  const hasSearchedOnce = useRef(false);

  useEffect(() => {
    if (searchValue && !hasSearchedOnce.current) {
      hasSearchedOnce.current = true;
    }
    if (hasSearchedOnce.current) {
      setQueryParams((prev) => ({ ...prev, page: 1, search: searchValue || undefined }));
    }
  }, [searchValue, userType]);

  const usermanagparams = `${userType}?${new URLSearchParams(
    Object.fromEntries(
      Object.entries(queryParams)
        .filter(([_, value]) => value !== undefined)
    )
  )}${Object.entries(filters)
    .map(([key, value]) => {
      if (value?.keywords) {
        const apiValue = typeof value.keywords === 'boolean' 
          ? value.keywords.toString() 
          : value.keywords;
        return `&${key}=${apiValue}`;
      }
      return '';
    })
    .filter(Boolean)
    .join("")}`;

  const {
    data: internalUsers,
    refetch,
    isFetching,
  } = useGetInternalUsersQuery({ usermanagparams });

  const textSx = {
    fontSize: "1.1em!important",
    lineHeight: "1.5em",
    height: "auto",
    overflow: "hidden",
    maxWidth: "100%",
    textOverflow: "ellipsis",
    whiteSpace: "normal",
    wordWrap: "break-word",
    minWidth: isMobile ? "100%" : "10vw",
  };

  const formatUserForTable = (data: IUserIT[] = []) => {
    return data?.map((item) => ({
      first_name: item.first_name,
      last_name: item.last_name,
      username: item.username,
      last_login: item.last_login,
      departments: item.departments,
      groups: item.groups,
      id: item.user_id,
      is_active: item.is_active ? "true" : "false",
      contact_id: item.contact_id,
    }));
  };

  const formattedData = formatUserForTable(internalUsers?.user_type_details);
  const headerLength = Object.keys(formattedData?.[0] ?? {}).length;
  const defaultColumnWidth = width ? (width / headerLength) * 1.6 : 200;

  const handleFilterSubmit = (filtersString: string) => {
    const filtersObj = JSON.parse(filtersString);
    setFilters(filtersObj);
    const newFilters: Record<string, string> = {};
    for (const key in filtersObj) {
      if (filtersObj[key]?.keywords) {
        if (key === "created_at" && typeof filtersObj[key].keywords === "string") {
          newFilters[key] = filtersObj[key].keywords;
        } else if (Array.isArray(filtersObj[key].keywords)) {
          newFilters[key] = filtersObj[key].keywords.join(",");
        }
      }
    }
    setQueryParams((prev) => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handleClearFilters = () => {
    setQueryParams({
      per_page: rowsPerPage,
      page: 1,
      search: undefined,
    });
    setFilters({});
    dispatch(clearAll());
    refetch();
  };

  const handlePageChange = (newPage: number, newPerPage: number) => {
    setQueryParams((prev) => ({
      ...prev,
      page: newPage,
      per_page: newPerPage
    }));
  };

  const customColumns = {
    first_name: {
      first_name: "First name",
      type: "text",
      flexGrow: 1,
      renderCell: ({ value }: { value: string }) => (
        <Typography sx={textSx}>{value}</Typography>
      ),
      link: {
        href: (params: any) => `${INCOMLEND_INTERNAL_LINK}${IT_LINK}/user-management/${params?.id}/manage`,
        target: "_self",
      },
    },
    last_name: { 
      last_name: "Last name", 
      type: "text",
      flexGrow: 1,
      renderCell: ({ value }: { value: string }) => (
        <Typography sx={textSx}>{value}</Typography>
      ),
      link: {
        href: (params: any) => `${INCOMLEND_INTERNAL_LINK}${IT_LINK}/user-management/${params?.id}/manage`,
        target: "_self",
      },
    },
    username: {
      username: "Username",
      type: "text",
      flexGrow: 1,
      renderCell: ({ value }: { value: string }) => (
        <Typography sx={textSx}>{value}</Typography>
      ),
      link: {
        href: (params: any) => `${INCOMLEND_INTERNAL_LINK}${IT_LINK}/user-management/${params?.id}/manage`,
        target: "_self",
      },
    },
    groups: {
      groups: "User Groups",
      type: "text",
      flexGrow: 1,
      renderCell: ({ value }: { value: string[] }) => {
        if (!value || value.length === 0) return (
          <Typography sx={textSx} color="grey">None</Typography>
        );
        if (value.length === 1)
          return (
            <Typography
              sx={{
                ...textSx,
                fontWeight: 700,
                p: 1,
                borderRadius: 1,
                border: `1px solid ${theme.palette.primary.main}`,
              }}
              key={`${0}-u-group`}
            >
              {value[0]}
            </Typography>
          );
        return (
          <ShowUserGroups
            items={value}
            title="Multiple Groups"
            aria="group"
          />
        );
      },
    },
    departments: {
      departments: "Departments",
      type: "text",
      flexGrow: 1,
      renderCell: ({ value }: { value: string[] }) => {
        if (!value || value.length === 0) return (
          <Typography sx={textSx} color="grey">None</Typography>
        );
        if (value.length === 1)
          return (
            <Typography
              sx={{
                ...textSx,
                fontWeight: 700,
                p: 1,
                borderRadius: 1,
                border: `1px solid ${theme.palette.primary.main}`,
              }}
              key={`${0}-u-department`}
            >
              {value[0]}
            </Typography>
          );
        return (
          <ShowUserGroups
            items={value}
            title="Multiple Departments"
            aria="department"
          />
        );
      },
    },
    is_active: { 
      is_active: "Active",
      type: "boolean",
      flexGrow: 1,
      renderCell: ({ value }: { value: string }) => {
        return (
          <Grid container justifyContent="center">
            <Grid item>
              {value === "true" ? (
                <CheckCircle style={{ color: "green" }} />
              ) : (
                <Cancel style={{ color: "red" }} />
              )}
            </Grid>
          </Grid>
        );
      },
    },
    last_login: {
      last_login: "Last login",
      type: "date",
      flexGrow: 1,
      renderCell: ({ value }: { value: Date }) => (
        <Typography sx={textSx}>
          {value ? new Date(value).toLocaleString() : "Never"}
        </Typography>
      ),
    },
  };

  return (
    <Slide in direction="left">
      <Box sx={{ flexGrow: 1, p: 0 }}>
        <Grid item xs={12} ref={ref} sx={{ margin: "5px" }}>
          <Grid container spacing={1}>
            <FormControl sx={{ m: 1, minWidth: 180 }} size="small">
              <InputLabel id="user-type-select-label">User Type</InputLabel>
              <Select
                labelId="user-type-select-label"
                id="user-type-select"
                value={userType}
                label="User Type"
                onChange={handleChange}
              >
                <MenuItem value="Internal">Internal</MenuItem>
                <MenuItem value="Client">Client</MenuItem>
                <MenuItem value="Sales_Partner">Sales Partner</MenuItem>
                <MenuItem value="Investor">Investor</MenuItem>
              </Select>
            </FormControl>
            
            {isFetching && <SkeletonLoad bars={10} />}
            {!isFetching && formattedData && (
              <DataTablev2
                disableFilters={false}
                clearFilters={handleClearFilters}
                onFilterSubmit={handleFilterSubmit}
                filterColumnTableName={internalUsers}
                pagination={internalUsers?.pagination}
                onPaginationChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                defaultColumnWidth={defaultColumnWidth}
                data={formattedData}
                toBeHidden={["contact_id"]}
                filters={filters}
                setFilters={setFilters}
                customColumns={customColumns}
                onRowClicked={(row, columnName) => {
                  if (columnName !== "groups" && columnName !== "departments") {
                    history.push(
                      `${INCOMLEND_INTERNAL_LINK}${IT_LINK}/user-management/${row?.id}/manage`
                    );
                  }
                }}
              />
            )}
            {!isFetching && (!formattedData || formattedData?.length === 0) && (
              <NoData text="No users found" sx={{ margin: "auto" }} />
            )}
          </Grid>
        </Grid>
      </Box>
    </Slide>
  );
}
